import React, { useEffect, useRef } from "react";
import { Box, Divider, IconButton, Paper, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import LinkIcon from "@mui/icons-material/Link";

const RichTextEditor = ({ value, onChange, minHeight = 180, placeholder }) => {
	const editorRef = useRef(null);
	const isInternalChange = useRef(false);

	useEffect(() => {
		if (!editorRef.current || isInternalChange.current) return;
		if (editorRef.current.innerHTML !== (value || "")) {
			editorRef.current.innerHTML = value || "";
		}
	}, [value]);

	const exec = (command, commandValue = null) => {
		editorRef.current?.focus();
		document.execCommand(command, false, commandValue);
		isInternalChange.current = true;
		onChange(editorRef.current?.innerHTML || "");
		isInternalChange.current = false;
	};

	const handleInput = () => {
		isInternalChange.current = true;
		onChange(editorRef.current?.innerHTML || "");
		isInternalChange.current = false;
	};

	const handleLink = () => {
		const url = window.prompt("Enter URL");
		if (url) {
			exec("createLink", url);
		}
	};

	return (
		<Paper variant="outlined" sx={{ overflow: "hidden" }}>
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 0.5,
					p: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
					bgcolor: "grey.50",
				}}>
				<Tooltip title="Bold">
					<IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("bold")}>
						<FormatBoldIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Italic">
					<IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={() => exec("italic")}>
						<FormatItalicIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Underline">
					<IconButton
						size="small"
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => exec("underline")}>
						<FormatUnderlinedIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Divider flexItem orientation="vertical" />
				<Tooltip title="Bullet list">
					<IconButton
						size="small"
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => exec("insertUnorderedList")}>
						<FormatListBulletedIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Numbered list">
					<IconButton
						size="small"
						onMouseDown={(e) => e.preventDefault()}
						onClick={() => exec("insertOrderedList")}>
						<FormatListNumberedIcon fontSize="small" />
					</IconButton>
				</Tooltip>
				<Tooltip title="Insert link">
					<IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={handleLink}>
						<LinkIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Box>
			<Box
				ref={editorRef}
				contentEditable
				suppressContentEditableWarning
				onInput={handleInput}
				data-placeholder={placeholder}
				sx={{
					minHeight,
					p: 2,
					outline: "none",
					"& ul, & ol": { pl: 3 },
					"& a": { color: "primary.main" },
					"&:empty:before": {
						content: "attr(data-placeholder)",
						color: "text.disabled",
					},
				}}
			/>
		</Paper>
	);
};

export default RichTextEditor;
